import React from 'react'
import { Query, Mutation } from 'react-apollo'
import gql from 'graphql-tag'

export interface DigestSettings {
  readonly compliance: boolean
  readonly remediation: boolean
  readonly library: boolean
}

export const organizationDigestSettingsQuery = gql`
  query OrganizationDigestSettings($organizationID: ID!) {
    organizationDigestSettings(organizationID: $organizationID) {
      id
      compliance
      remediation
      library
    }
  }
`

export function OrganizationDigestSettingsQuery({
  organizationID,
  children
}: {
  readonly organizationID: string
  readonly children: (props: DigestSettings) => React.ReactNode
}) {
  return (
    <Query<
      { readonly organizationDigestSettings: DigestSettings },
      {
        readonly organizationID: string
      }
    >
      query={organizationDigestSettingsQuery}
      variables={{ organizationID }}
      fetchPolicy="network-only"
    >
      {({ data, error, loading }) => {
        if (error || loading || !data) {
          return <div />
        }

        const { organizationDigestSettings } = data

        return children(organizationDigestSettings)
      }}
    </Query>
  )
}

export const userDigestSettingsQuery = gql`
  query UserDigestSettings($organizationID: ID!, $viewerID: ID!) {
    userDigestSettings(organizationID: $organizationID, viewerID: $viewerID) {
      id
      compliance
      remediation
      library
    }
  }
`

export function UserDigestSettingsQuery({
  organizationID,
  viewerID,
  children
}: {
  readonly organizationID: string
  readonly viewerID: string
  readonly children: (props: DigestSettings) => React.ReactNode
}) {
  return (
    <Query<
      { readonly userDigestSettings: DigestSettings },
      {
        readonly organizationID: string
        readonly viewerID: string
      }
    >
      query={userDigestSettingsQuery}
      variables={{ organizationID, viewerID }}
      fetchPolicy="network-only"
    >
      {({ data, error, loading }) => {
        if (error || loading || !data) {
          return <div />
        }

        const { userDigestSettings } = data

        return children(userDigestSettings)
      }}
    </Query>
  )
}

export const updateOrganizationDigestSettingsMutation = gql`
  mutation UpdateOrganizationDigestSettings(
    $organizationID: ID!
    $compliance: Boolean!
    $remediation: Boolean!
    $library: Boolean!
  ) {
    updateOrganizationDigestSettings(
      input: {
        organizationID: $organizationID
        compliance: $compliance
        remediation: $remediation
        library: $library
      }
    ) {
      id
      compliance
      remediation
      library
    }
  }
`

interface UpdateOrganizationDigestSettingsArgs extends DigestSettings {
  readonly organizationID: string
}

type UpdateOrganizationDigestSettings = (
  args: UpdateOrganizationDigestSettingsArgs
) => Promise<any>

export function UpdateOrganizationDigestSettingsMutation({
  children
}: {
  readonly children: (
    updateDigestSettings: UpdateOrganizationDigestSettings
  ) => React.ReactNode
}) {
  return (
    <Mutation<{}, UpdateOrganizationDigestSettingsArgs>
      mutation={updateOrganizationDigestSettingsMutation}
    >
      {(updateDigestSettings) =>
        children(async (args: UpdateOrganizationDigestSettingsArgs) =>
          updateDigestSettings({
            variables: args,

            optimisticResponse: {
              updateOrganizationDigestSettings: {
                id: args.organizationID,
                compliance: args.compliance,
                remediation: args.remediation,
                library: args.library,
                __typename: 'OrganizationDigestSettings'
              }
            }
          })
        )
      }
    </Mutation>
  )
}

export const updateUserDigestSettingsMutation = gql`
  mutation UpdateUserDigestSettings(
    $organizationID: ID!
    $viewerID: ID!
    $compliance: Boolean!
    $remediation: Boolean!
    $library: Boolean!
  ) {
    updateUserDigestSettings(
      input: {
        organizationID: $organizationID
        viewerID: $viewerID
        compliance: $compliance
        remediation: $remediation
        library: $library
      }
    ) {
      id
      compliance
      remediation
      library
    }
  }
`

interface UpdateUserDigestSettingsArgs
  extends UpdateOrganizationDigestSettingsArgs {
  readonly viewerID: string
}

type UpdateUserDigestSettings = (
  args: UpdateUserDigestSettingsArgs
) => Promise<any>

export function UpdateUserDigestSettingsMutation({
  children
}: {
  readonly children: (
    updateDigestSettings: UpdateUserDigestSettings
  ) => React.ReactNode
}) {
  return (
    <Mutation<{}, UpdateUserDigestSettingsArgs>
      mutation={updateUserDigestSettingsMutation}
    >
      {(updateDigestSettings) =>
        children(async (args: UpdateUserDigestSettingsArgs) =>
          updateDigestSettings({
            variables: args,

            optimisticResponse: {
              updateUserDigestSettings: {
                id: `${args.organizationID}|${args.viewerID}`,
                compliance: args.compliance,
                remediation: args.remediation,
                library: args.library,
                __typename: 'UserDigestSettings'
              }
            }
          })
        )
      }
    </Mutation>
  )
}
